import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/common/AppModal';
import { Alert, Badge, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Plus, Trash2 } from 'lucide-react';
import type { MultiValue } from 'react-select';
import Flex from '@/components/common/Flex';
import MultiSelect from '@/components/common/MultiSelect';
import {
  toMcpApiErrorMessage,
  useCreateMcpPromptTemplateMutation,
  useGetMcpPromptTemplateQuery,
  useListMcpPromptCategoriesQuery,
  useListMcpPromptVariablesQuery,
  useListMcpToolTypesQuery,
  useUpdateMcpPromptTemplateMutation
} from '../api/mcpApi';
import type {
  McpPromptTemplate,
  McpPromptVariable
} from '../types/mcpApiTypes';

type InputDraft = {
  key: string;
  label: string;
  description: string;
  isRequired: boolean;
};

type FormState = {
  title: string;
  description: string;
  userPrompt: string;
  languageCode: string;
  visibility: 'private' | 'public';
  toolIds: string[];
  categoryIds: string[];
  inputs: InputDraft[];
};

type SelectOption = { value: string; label: string };

const EMPTY_FORM: FormState = {
  title: '',
  description: '',
  userPrompt: '',
  languageCode: 'en',
  visibility: 'private',
  toolIds: [],
  categoryIds: [],
  inputs: []
};

const SELECT_STYLES = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: 31,
    fontSize: '0.8125rem',
    boxShadow: 'none',
    cursor: 'pointer'
  }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    padding: '0 8px',
    gap: 4
  }),
  multiValue: (base: Record<string, unknown>) => ({
    ...base,
    margin: 0
  }),
  indicatorsContainer: (base: Record<string, unknown>) => ({
    ...base,
    height: 31
  }),
  dropdownIndicator: (base: Record<string, unknown>) => ({
    ...base,
    padding: '4px 8px'
  }),
  clearIndicator: (base: Record<string, unknown>) => ({
    ...base,
    padding: '4px 8px'
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    fontSize: '0.8125rem',
    zIndex: 1060
  }),
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 1060
  })
};

type Props = {
  show: boolean;
  editId: string | null;
  onHide: () => void;
  onSaved: (template: McpPromptTemplate) => void;
};

export function PromptTemplateFormModal({
  show,
  editId,
  onHide,
  onSaved
}: Props) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const isEdit = Boolean(editId);

  const { data: variables = [], isFetching: loadingVariables } =
    useListMcpPromptVariablesQuery(undefined, { skip: !show });
  const { data: categories = [], isFetching: loadingCategories } =
    useListMcpPromptCategoriesQuery(undefined, { skip: !show });
  const { data: toolTypes = [], isFetching: loadingTools } =
    useListMcpToolTypesQuery(undefined, { skip: !show });
  const {
    data: detail,
    isFetching: loadingDetail,
    isError: detailFailed,
    error: detailErrorPayload
  } = useGetMcpPromptTemplateQuery(editId ?? '', {
    skip: !show || !editId
  });

  const [createTemplate, { isLoading: creating }] =
    useCreateMcpPromptTemplateMutation();
  const [updateTemplate, { isLoading: updating }] =
    useUpdateMcpPromptTemplateMutation();

  const loading =
    loadingVariables ||
    loadingCategories ||
    loadingTools ||
    (Boolean(editId) && loadingDetail);
  const saving = creating || updating;

  const categoryOptions = useMemo<SelectOption[]>(
    () =>
      categories.map(category => ({
        value: category.id,
        label: category.title
      })),
    [categories]
  );

  const toolOptions = useMemo<SelectOption[]>(
    () =>
      toolTypes.map(tool => ({
        value: tool.id,
        label: tool.title
      })),
    [toolTypes]
  );

  const selectedCategories = useMemo(
    () =>
      categoryOptions.filter(option => form.categoryIds.includes(option.value)),
    [categoryOptions, form.categoryIds]
  );

  const selectedTools = useMemo(
    () => toolOptions.filter(option => form.toolIds.includes(option.value)),
    [toolOptions, form.toolIds]
  );

  useEffect(() => {
    if (!show) return;
    setError(null);
    setNotice(null);
    if (!editId) {
      setForm(EMPTY_FORM);
    }
  }, [show, editId]);

  useEffect(() => {
    if (!show || !editId || !detail) return;

    if (detail.hasPendingUpdate && !detail.parentId) {
      setNotice(
        'این قالب عمومی تاییدشده است. ذخیرهٔ تغییرات به‌عنوان نسخهٔ در انتظار تایید ثبت می‌شود.'
      );
    } else if (detail.parentId) {
      setNotice('در حال ویرایش نسخهٔ در انتظار تایید این قالب هستید.');
    } else {
      setNotice(null);
    }

    setForm({
      title: detail.title,
      description: detail.description ?? '',
      userPrompt: detail.userPrompt ?? '',
      languageCode: detail.languageCode || 'en',
      visibility: detail.visibility,
      toolIds: detail.toolIds ?? [],
      categoryIds: detail.categoryIds ?? [],
      inputs: (detail.inputs ?? []).map(input => ({
        key: input.key,
        label: input.label,
        description: input.description ?? '',
        isRequired: input.isRequired
      }))
    });
  }, [detail, editId, show]);

  useEffect(() => {
    if (!show || !editId || !detailFailed) return;
    setError(
      toMcpApiErrorMessage(detailErrorPayload, 'بارگذاری قالب ناموفق بود')
    );
  }, [detailErrorPayload, detailFailed, editId, show]);

  const addEmptyInput = () => {
    setForm(prev => ({
      ...prev,
      inputs: [
        ...prev.inputs,
        {
          key: '',
          label: '',
          description: '',
          isRequired: true
        }
      ]
    }));
  };

  const addVariable = (variable: McpPromptVariable) => {
    setForm(prev => {
      if (prev.inputs.some(input => input.key === variable.key)) return prev;
      return {
        ...prev,
        inputs: [
          ...prev.inputs,
          {
            key: variable.key,
            label: variable.title,
            description: variable.description ?? '',
            isRequired: true
          }
        ]
      };
    });
  };

  const updateInput = (index: number, patch: Partial<InputDraft>) => {
    setForm(prev => ({
      ...prev,
      inputs: prev.inputs.map((input, i) =>
        i === index ? { ...input, ...patch } : input
      )
    }));
  };

  const removeInput = (index: number) => {
    setForm(prev => ({
      ...prev,
      inputs: prev.inputs.filter((_, i) => i !== index)
    }));
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!form.title.trim() || !form.userPrompt.trim()) {
      setError('عنوان و متن قالب الزامی است');
      return;
    }

    for (const input of form.inputs) {
      if (!input.key.trim() || !input.label.trim()) {
        setError('کلید و برچسب هر متغیر الزامی است');
        return;
      }
    }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      userPrompt: form.userPrompt,
      languageCode: form.languageCode.trim() || 'en',
      visibility: form.visibility,
      toolIds: form.toolIds,
      categoryIds: form.categoryIds,
      inputs: form.inputs.map((input, index) => ({
        key: input.key.trim(),
        label: input.label.trim(),
        description: input.description.trim() || null,
        isRequired: input.isRequired,
        sortOrder: index
      }))
    };

    try {
      const saved = editId
        ? await updateTemplate({ id: editId, body: payload }).unwrap()
        : await createTemplate(payload).unwrap();
      onSaved(saved);
    } catch (err) {
      setError(toMcpApiErrorMessage(err, 'ذخیره ناموفق بود'));
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton className="py-2 px-3">
        <Modal.Title className="fs-0">
          {isEdit ? 'ویرایش قالب پرامپت' : 'ساخت قالب پرامپت'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        className="py-2 px-3"
        style={{ maxHeight: 'min(70vh, calc(100vh - 9rem))', overflowY: 'auto' }}
      >
        {loading ? (
          <Flex
            alignItems="center"
            justifyContent="center"
            className="py-4 text-500"
          >
            <Spinner animation="border" size="sm" className="me-2" />
            در حال بارگذاری…
          </Flex>
        ) : (
          <Form
            id="mcp-prompt-template-form"
            onSubmit={event => void onSubmit(event)}
          >
            {error ? (
              <Alert variant="danger" className="py-2 px-3 mb-2 fs--1">
                {error}
              </Alert>
            ) : null}
            {notice ? (
              <Alert variant="info" className="py-2 px-3 mb-2 fs--1">
                {notice}
              </Alert>
            ) : null}

            <Row className="g-2">
              <Col md={8}>
                <Form.Group controlId="prompt-title">
                  <Form.Label className="fs--1 mb-1">عنوان</Form.Label>
                  <Form.Control
                    size="sm"
                    value={form.title}
                    onChange={event =>
                      setForm(prev => ({
                        ...prev,
                        title: event.target.value
                      }))
                    }
                    placeholder="مثلاً تحلیل افت رتبه کلمات"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="prompt-lang">
                  <Form.Label className="fs--1 mb-1">زبان</Form.Label>
                  <Form.Select
                    size="sm"
                    value={form.languageCode}
                    onChange={event =>
                      setForm(prev => ({
                        ...prev,
                        languageCode: event.target.value
                      }))
                    }
                  >
                    <option value="en">English</option>
                    <option value="fa">فارسی</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group controlId="prompt-desc">
                  <Form.Label className="fs--1 mb-1">توضیحات عمومی</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    value={form.description}
                    onChange={event =>
                      setForm(prev => ({
                        ...prev,
                        description: event.target.value
                      }))
                    }
                    placeholder="در کتابخانه قالب‌ها نمایش داده می‌شود"
                  />
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group controlId="prompt-body">
                  <Form.Label className="fs--1 mb-1">قالب پرامپت</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    size="sm"
                    value={form.userPrompt}
                    onChange={event =>
                      setForm(prev => ({
                        ...prev,
                        userPrompt: event.target.value
                      }))
                    }
                    placeholder="Use {{variable_key}} for inputs…"
                    required
                    className="font-monospace"
                    style={{ direction: 'ltr', textAlign: 'left' }}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Label className="fs--1 mb-1 d-block">نمایش</Form.Label>
                <Form.Select
                  size="sm"
                  value={form.visibility}
                  onChange={event =>
                    setForm(prev => ({
                      ...prev,
                      visibility: event.target.value as 'private' | 'public'
                    }))
                  }
                >
                  <option value="private">خصوصی — فقط خودم</option>
                  <option value="public">عمومی — نیاز به تایید</option>
                </Form.Select>
              </Col>

              <Col md={4}>
                <Form.Label className="fs--1 mb-1 d-block">دسته‌بندی</Form.Label>
                <MultiSelect
                  options={categoryOptions}
                  value={selectedCategories}
                  onChange={value =>
                    setForm(prev => ({
                      ...prev,
                      categoryIds: (
                        (value as MultiValue<SelectOption>) ?? []
                      ).map(option => option.value)
                    }))
                  }
                  placeholder="انتخاب دسته…"
                  isClearable
                  isSearchable
                  isDisabled={categoryOptions.length === 0}
                  hideSelectedOptions={false}
                  menuPortalTarget={
                    typeof document !== 'undefined' ? document.body : null
                  }
                  styles={SELECT_STYLES as never}
                />
              </Col>

              <Col md={4}>
                <Form.Label className="fs--1 mb-1 d-block">
                  مرتبط به ابزار
                </Form.Label>
                <MultiSelect
                  options={toolOptions}
                  value={selectedTools}
                  onChange={value =>
                    setForm(prev => ({
                      ...prev,
                      toolIds: ((value as MultiValue<SelectOption>) ?? []).map(
                        option => option.value
                      )
                    }))
                  }
                  placeholder="انتخاب ابزار…"
                  isClearable
                  isSearchable
                  hideSelectedOptions={false}
                  menuPortalTarget={
                    typeof document !== 'undefined' ? document.body : null
                  }
                  styles={SELECT_STYLES as never}
                />
              </Col>

              <Col xs={12}>
                <Flex
                  alignItems="center"
                  justifyContent="between"
                  wrap="wrap"
                  className="gap-2 mb-1"
                >
                  <Form.Label className="fs--1 mb-0">متغیرها</Form.Label>
                  <Flex className="gap-1 flex-wrap align-items-center">
                    {variables.map(variable => (
                      <Badge
                        key={variable.id}
                        bg="light"
                        text="dark"
                        className="border cursor-pointer fs--2 fw-normal"
                        role="button"
                        onClick={() => addVariable(variable)}
                        title={variable.description ?? variable.title}
                      >
                        + {variable.title}
                      </Badge>
                    ))}
                    <Button
                      type="button"
                      size="sm"
                      variant="falcon-default"
                      className="py-0 px-2"
                      onClick={addEmptyInput}
                    >
                      <Plus size={12} className="me-1" />
                      جدید
                    </Button>
                  </Flex>
                </Flex>

                {form.inputs.length === 0 ? (
                  <div className="fs--2 text-500 border rounded-2 px-2 py-2 bg-body-tertiary">
                    در صورت نیاز به دامنه/URL، متغیر اضافه کنید.
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {form.inputs.map((input, index) => (
                      <div
                        key={`${index}-${input.key}`}
                        className="border rounded-2 px-2 py-2"
                      >
                        <Row className="g-1 align-items-center">
                          <Col xs={6} md={4}>
                            <Form.Control
                              size="sm"
                              value={input.key}
                              onChange={event =>
                                updateInput(index, {
                                  key: event.target.value
                                })
                              }
                              placeholder="key"
                              style={{ direction: 'ltr', textAlign: 'left' }}
                            />
                          </Col>
                          <Col xs={6} md={4}>
                            <Form.Control
                              size="sm"
                              value={input.label}
                              onChange={event =>
                                updateInput(index, {
                                  label: event.target.value
                                })
                              }
                              placeholder="برچسب"
                            />
                          </Col>
                          <Col xs={4} md={3}>
                            <Form.Check
                              type="switch"
                              id={`input-required-${index}`}
                              label={<span className="fs--2">الزامی</span>}
                              checked={input.isRequired}
                              onChange={event =>
                                updateInput(index, {
                                  isRequired: event.target.checked
                                })
                              }
                            />
                          </Col>
                          <Col xs={2} md={1} className="text-end">
                            <Button
                              type="button"
                              size="sm"
                              variant="falcon-danger"
                              className="py-0 px-1"
                              onClick={() => removeInput(index)}
                              aria-label="حذف متغیر"
                            >
                              <Trash2 size={13} />
                            </Button>
                          </Col>
                        </Row>
                        <Form.Control
                          size="sm"
                          className="mt-1"
                          value={input.description}
                          onChange={event =>
                            updateInput(index, {
                              description: event.target.value
                            })
                          }
                          placeholder="توضیح (اختیاری)"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer className="py-2 px-3">
        <Button
          type="button"
          variant="link"
          size="sm"
          className="text-600"
          onClick={onHide}
          disabled={saving}
        >
          انصراف
        </Button>
        <Button
          type="submit"
          form="mcp-prompt-template-form"
          variant="primary"
          size="sm"
          disabled={loading || saving}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              ذخیره…
            </>
          ) : isEdit ? (
            'ذخیره تغییرات'
          ) : (
            'ایجاد قالب'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
